import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar,
  Stack
} from '@mui/material';
import { 
  Business as BusinessIcon, 
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { createOrganizationForUser } from '../services/organizationService';
import { getUserPendingJoinRequests } from '../services/joinRequestService';
import OnboardingHeader from '../components/OnboardingHeader';

function OrganizationChoicePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state) => state.user.user);
  
  // State for create organization dialog
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);

  // Load user's pending join requests on component mount
  useEffect(() => {
    const loadPendingRequests = async () => {
      try {
        const requests = await getUserPendingJoinRequests();
        setPendingRequests(requests);
      } catch (err) {
        console.error('Error loading pending requests:', err);
      } finally {
        setPendingLoading(false);
      }
    };

    loadPendingRequests();
  }, []);


  const handleCreateOrganization = async () => {
    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await createOrganizationForUser({ name: orgName });
      
      enqueueSnackbar('Organization created successfully! Starting setup...', { variant: 'success' });
      
      // Refresh user data
      await dispatch(fetchCurrentUser());
      
      // Close dialog
      setCreateOrgDialogOpen(false);
      setOrgName('');
      
      // Navigate to onboarding
      navigate('/onboarding/welcome');
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.response?.data?.error || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };


  const quickCreateOrganization = async () => {
    try {
      setLoading(true);
      setError('');

      // Build a sensible default name; user can edit on the next page
      const defaultNameBase = user?.firstName || user?.username || 'My';
      const defaultName = `${defaultNameBase} Organization`;

      await createOrganizationForUser({ name: defaultName });

      enqueueSnackbar('Organization created! Continue setup.', { variant: 'success' });

      // Refresh user and navigate to onboarding
      await dispatch(fetchCurrentUser());
      navigate('/onboarding/welcome');
    } catch (err) {
      console.error('Error creating organization:', err);
      enqueueSnackbar(err.response?.data?.error || 'Failed to create organization', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinOrgClick = () => {
    navigate('/search-organization');
  };

  const handleCloseCreateDialog = () => {
    setCreateOrgDialogOpen(false);
    setOrgName('');
    setError('');
  };

  // Card data for the new design
  const cardData = [
    {
      title: "Create a new organization",
      description: "Set up a new organization for your team.\nYou'll be the admin and will be able to invite your team members.",
      buttonText: "Create organization",
      buttonVariant: "contained",
      buttonWidth: "231px",
      onClick: quickCreateOrganization,
      icon: <BusinessIcon sx={{ fontSize: 120, color: '#d9d9d9' }} />
    },
    {
      title: "Join an existing organization", 
      description: "Search for an organization by ID or name and request to join.",
      buttonText: "Search organizations",
      buttonVariant: "outlined",
      buttonWidth: "242px",
      onClick: handleJoinOrgClick,
      icon: <PersonAddIcon sx={{ fontSize: 120, color: '#d9d9d9' }} />
    },
  ];

  return (
    <>
      <OnboardingHeader />
      
      {/* Pending Join Requests Section - Keep existing functionality */}
      {!pendingLoading && pendingRequests.length > 0 && (
        <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pending Join Requests
            </Typography>
            <Typography variant="body2">
              You have {pendingRequests.length} pending join request(s). You can still create a new organization or request to join another organization while waiting for approval.
            </Typography>
          </Alert>
          
          {pendingRequests.map((request) => (
            <Box key={request._id} sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={request.organizationId?.logo?.thumbnailUrl || request.organizationId?.logo?.url}
                  sx={{ width: 32, height: 32 }}
                >
                  <BusinessIcon />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {request.organizationId?.name || 'Unknown Organization'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Request submitted: {new Date(request.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Container>
      )}

      {/* New Figma Design Implementation */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: "950px",
            margin: "0 auto",
            mt: 4,
            mb: 4
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "548px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'text.primary',
                fontSize: "50px",
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                textAlign: "center",
                width: "100%",
                mt: -0.125,
              }}
            >
              Hello {user?.firstName || user?.username || 'there'}!
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.secondary',
                fontSize: "24px",
                fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                fontWeight: 500,
                textAlign: "center",
                width: "100%",
              }}
            >
              Choose how you&apos;d like to get started
            </Typography>
          </Box>

          {/* Cards Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {cardData.map((card, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{
                  width: "462px",
                  height: "409px",
                  borderRadius: "19px",
                  border: "1px solid",
                  borderColor: 'divider',
                  boxShadow: "0px 4px 5.9px rgba(0, 0, 0, 0.1)",
                  padding: "50px 64px 10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 8px 15px rgba(0, 0, 0, 0.15)',
                  }
                }}
                onClick={card.onClick}
              >
                <Stack
                  width={index === 0 ? "330px" : "333px"}
                  alignItems="center"
                  spacing={index === 0 ? 4.25 : 7.25}
                >
                  <Stack alignItems="center" spacing={1.5} width="100%">
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        bgcolor: "#d9d9d9",
                        borderRadius: "60px",
                      }}
                    >
                      {card.icon}
                    </Avatar>
                    <Typography
                      variant="h3"
                      sx={{
                        color: 'text.primary',
                        fontSize: "23px",
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        fontWeight: 500,
                        textAlign: index === 0 ? "center" : "left",
                        width: "100%",
                      }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontSize: "16px",
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        fontWeight: 400,
                        textAlign: "center",
                        width: "100%",
                        whiteSpace: 'pre-line'
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Stack>

                  <Box width={card.buttonWidth} height="43px">
                    <Button
                      variant={card.buttonVariant}
                      fullWidth
                      onClick={card.onClick}
                      sx={{
                        height: "43px",
                        borderRadius: "2px",
                        textTransform: "none",
                        fontFamily: "'Poppins', 'Roboto', 'Helvetica', 'Arial', sans-serif",
                        fontWeight: card.buttonVariant === "contained" ? 600 : 400,
                        fontSize: "16px",
                        boxShadow: "0px 13px 9.7px -8px rgba(32, 141, 244, 0.24)",
                        color: card.buttonVariant === "contained" ? "white" : "primary.main",
                        backgroundColor: card.buttonVariant === "contained" ? "primary.main" : "transparent",
                        borderColor: "primary.main",
                        '&:hover': {
                          backgroundColor: card.buttonVariant === "contained" ? "primary.dark" : "primary.main",
                          color: "white",
                          transform: 'translateY(-1px)',
                        }
                      }}
                    >
                      {card.buttonText}
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
        </Box>
      </Container>

        {/* Note: Organization name will be set on the next page; dialog removed for a smoother flow */}

    </>
  );
}

export default OrganizationChoicePage; 